import React, { useState } from "react";
import { Handshake, AlertCircle, Building2 } from "lucide-react";
import type { DiscoveryInfluencerProfile } from "../../types/discoveryProfile.types";

export interface CollaborationTabProps {
  influencer: DiscoveryInfluencerProfile;
}

export function CollaborationTab({ influencer }: CollaborationTabProps) {
  const [visibleCards, setVisibleCards] = useState(3);
  const hasBrandData = influencer.pastCollaboratedBrands.length > 0;

  const handleLoadMore = () => {
    setVisibleCards((prev) => prev + 3);
  };

  return (
    <div className="space-y-6 px-6 py-2 max-w-[950px]">
      {/* Top Section: Total Brand Collaborations */}
      <div className="bg-[#F8F8F8] border border-[#CFCFCF] rounded-[20px] max-h-[180px] max-w-[920px] p-8 relative overflow-hidden flex justify-between items-center shadow-sm">
        {/* Background Faded Handshake Watermark */}
        <div className="absolute right-[0px] bottom-[-10px] opacity-[0.03] pointer-events-none">
          <Handshake size={80} />
        </div>

        <div className="flex flex-col gap-6 z-10 pl-4">
          <h2 className="text-2xl font-semibold text-black underline decoration-2 underline-offset-4">
            Total Brand Collaborations
          </h2>
          <span className="text-[40px] font-bold font-['Inter'] italic text-[#633A89]">
            {influencer.pastCollaboratedBrandsCount}
          </span>
        </div>

        {/* Right Side Icon Container */}
        <div className="z-10 flex items-center justify-center w-[110px] h-[110px] mr-6">
          <img
            src="/images/Dashboard/Collab.png"
            alt="Collaboration"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Bottom Section: Past Brand Collaborations */}
      <div>
        <h2 className="text-2xl font-bold mb-5 text-black">
          Past Brand Collaborations
        </h2>

        {hasBrandData ? (
          <>
            {/* Grid of Cards */}
            <div className="grid grid-cols-3 gap-16">
              {influencer.pastCollaboratedBrands.slice(0, visibleCards).map((brand, index) => (
                <div
                  key={index}
                  className="bg-[#E6DFFF] rounded-2xl border border-[#CFCFCF] py-5 px-6 flex flex-col justify-between h-[160px] w-[300px]"
                >
                  {/* White Logo Box */}
                  <div className="w-[68px] h-[68px] bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm">
                    {brand.brand_logo ? (
                      <img
                        src={brand.brand_logo}
                        alt={brand.brand_name}
                        className="w-[55px] h-[55px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <Building2 className="w-7 h-7 text-muted-foreground" />
                    )}
                  </div>

                  {/* Brand Name */}
                  <span className="font-semibold text-lg text-black tracking-tight mt-auto">
                    {brand.brand_name}
                  </span>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {visibleCards < influencer.pastCollaboratedBrands.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="text-black font-semibold text-lg underline italic cursor-pointer"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 bg-muted/30 rounded-lg">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-foreground font-medium mb-2">No Brand Collaborations Yet</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {influencer.name} doesn't have any recorded brand collaborations yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CollaborationTab;
